/**
 * Sport IQ — Course Hub landing (basketball / football / soccer / track).
 * Renders per-sport branding via SPORT_META. Attribution: Created by Coach B.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, BookOpen, GraduationCap, Layers, ArrowRight, Target, Search, Zap } from 'lucide-react';
import api from '../services/api';
import { AppliedMasteryElective } from '../components/AppliedMasteryBanner';
import { getSportMeta } from '../lib/sportMeta';

const GOLD = '#D4AF37';

const BasketballIQ = ({ sport = 'basketball' }) => {
  const meta = getSportMeta(sport);
  const ACCENT = meta.accent;
  const [glossaryPreview, setGlossaryPreview] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/academy/${sport}-iq/glossary`),
      api.get(`/academy/${sport}-iq/course`),
    ])
      .then(([g, c]) => {
        setGlossaryPreview(g.data);
        setCourse(c.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [sport]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050508] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: ACCENT }} />
      </div>
    );
  }

  const totalModules = course?.total_modules || 0;

  return (
    <div className="min-h-screen bg-[#050508] text-white pt-16 pb-24" data-testid={`${sport}-iq-hub`}>
      {/* Hero */}
      <div className="relative py-14 md:py-20 px-6 border-b-2 border-zinc-900 overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(${ACCENT} 1px, transparent 1px), linear-gradient(90deg, ${ACCENT} 1px, transparent 1px)`,
            backgroundSize: '54px 54px',
          }}
        />
        <div className="relative container mx-auto max-w-6xl">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: ACCENT }}>
            <Target className="w-3 h-3" /> {meta.hub_title}
          </div>
          <h1 className="text-4xl md:text-6xl font-black uppercase tracking-tight mt-3 leading-[1.02]">
            The IQ of<br />
            <span style={{ color: ACCENT }}>{meta.label}.</span>
          </h1>
          <p className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-500 mt-3">
            {meta.tagline}
          </p>
          <p className="text-zinc-400 text-sm md:text-base max-w-2xl mt-5">
            {meta.intro}
          </p>
          <div className="flex flex-wrap items-center gap-3 mt-6">
            <div className="border-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest" style={{ borderColor: `${ACCENT}55`, color: ACCENT }}>
              The Full Glossary
            </div>
            <div className="border-2 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest" style={{ borderColor: `${GOLD}55`, color: GOLD }}>
              {totalModules > 0 ? '6-Week Course · Live' : '6-Week Course · Coming Soon'}
            </div>
            <div className="border-2 border-zinc-800 px-3 py-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-300">
              Always Growing
            </div>
          </div>
        </div>
      </div>

      {/* Applied Mastery quote */}
      <div className="container mx-auto max-w-6xl px-6 pt-10">
        <AppliedMasteryElective />
      </div>

      {/* Three entry-points */}
      <div className="container mx-auto max-w-6xl px-6 py-12 grid md:grid-cols-3 gap-5">
        {/* Glossary */}
        <Link
          to={`/${sport}-iq/glossary`}
          className="group relative border-2 border-zinc-800 bg-zinc-950 p-6 hover:border-orange-500 transition-all"
          data-testid="entry-glossary"
          style={{ borderTopColor: ACCENT, borderTopWidth: 3 }}
        >
          <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: ACCENT }}>
            <BookOpen className="w-3 h-3" /> Reference
          </div>
          <h3 className="text-2xl font-black uppercase mt-2 leading-tight">Glossary Library</h3>
          <p className="text-sm text-zinc-400 mt-2">
            Click any term for the full explainer: definition, when to use, when NOT to,
            and the counters that punish over-committed defenses.
          </p>
          <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-wider mt-4 opacity-70 group-hover:opacity-100" style={{ color: ACCENT }}>
            Enter the glossary <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </Link>

        {/* 6-Week Course */}
        <Link
          to={`/${sport}-iq/course`}
          className="group relative border-2 border-zinc-800 bg-zinc-950 p-6 hover:border-yellow-500 transition-all"
          data-testid="entry-course"
          style={{ borderTopColor: GOLD, borderTopWidth: 3 }}
        >
          <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: GOLD }}>
            <GraduationCap className="w-3 h-3" /> Immersive
          </div>
          <h3 className="text-2xl font-black uppercase mt-2 leading-tight">6-Week Course</h3>
          <p className="text-sm text-zinc-400 mt-2">
            30 minutes a day. Six weeks. Structured modules narrated by Coach B in his own cloned voice. End as a student of the game.
          </p>
          <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-wider mt-4 opacity-70 group-hover:opacity-100" style={{ color: GOLD }}>
            {totalModules > 0 ? 'Open the course' : 'Coming soon'} <ArrowRight className="w-3.5 h-3.5" />
          </div>
        </Link>

        {/* Deep-Dive */}
        <div
          className="relative border-2 border-zinc-800 bg-zinc-950 p-6 opacity-70"
          data-testid="entry-deepdive"
          style={{ borderTopColor: '#a3a3a3', borderTopWidth: 3 }}
        >
          <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase font-black text-zinc-400">
            <Layers className="w-3 h-3" /> Masterclass
          </div>
          <h3 className="text-2xl font-black uppercase mt-2 leading-tight">Deep-Dive Series</h3>
          <p className="text-sm text-zinc-400 mt-2">
            Long-form masterclasses on requested subjects — the deep readings on the concepts you tell us matter most.
          </p>
          <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-wider mt-4 text-zinc-500">
            Growing every week
          </div>
        </div>
      </div>

      {/* Featured terms strip */}
      {glossaryPreview?.terms?.length > 0 && (
        <div className="container mx-auto max-w-6xl px-6 pb-12">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase font-black" style={{ color: ACCENT }}>
              <Zap className="w-3 h-3" /> Featured Terms
            </div>
            <Link to={`/${sport}-iq/glossary`} className="text-[11px] font-black uppercase tracking-wider text-zinc-400 hover:text-white flex items-center gap-1">
              <Search className="w-3.5 h-3.5" /> Browse all
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {glossaryPreview.terms.slice(0, 8).map((t) => (
              <Link
                key={t.slug}
                to={`/${sport}-iq/term/${t.slug}`}
                className="border border-zinc-800 bg-zinc-950 p-4 hover:border-orange-500 transition-all"
                data-testid={`featured-term-${t.slug}`}
              >
                <div className="text-[9px] tracking-[0.3em] uppercase font-black text-zinc-500">{t.category?.replace(/-/g, ' ')}</div>
                <h4 className="text-white font-black text-sm mt-1">{t.term}</h4>
                <p className="text-[11px] text-zinc-400 mt-1 line-clamp-2">{t.one_line}</p>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Attribution */}
      <div className="container mx-auto max-w-6xl px-6 py-6 border-t border-zinc-900">
        <div className="text-[10px] tracking-[0.4em] uppercase font-black text-zinc-600 text-center">
          {glossaryPreview?.attribution || 'Created by Coach B · Scholar Athlete Academy'}
        </div>
      </div>
    </div>
  );
};

export default BasketballIQ;
